/**
 * AOP 2027 Planning — review and approval on a department tab (David, Oct 19–20 2026).
 *
 * Under the iteration bar's state line: who has ticked "reviewed" so far, and the actions this
 * caller has —
 *   - Karen, Patrick, Florian: "Mark reviewed" on a submitted department.
 *   - Anna and David: "Tick for <name>" for an absent reviewer, ONE approve button —
 *     "Include in <iteration>" while ticks are missing (Oct 29 2026: ticks for whoever is still
 *     missing and approves, after a confirmation naming them; named "Approve now" until David's
 *     wording of Oct 29 2026), else "Approve" (greyed out, with the reason,
 *     while the drafts changed) — and "Reopen" (note) on an approved department.
 * The reviewers show as chips; a tick made on someone's behalf names who made it under the chip.
 * No "Send back" (David, Oct 29 2026): a role is set aside or edited one by one on the Approvals tab.
 * Every rule is the proxy's (`proxy-server/lib/aopReviews.js`); the buttons appear only where it
 * would allow them and its refusal is shown word for word if it does not.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import {
  AOP_TIMELINE_QUERY_KEY,
  type AopIterationStatus,
  approveAopDepartment,
  reopenAopDepartment,
  reviewAopDepartment,
} from '../../services/aopPlanningApi';
import { personName } from './aopFormat';

const MAX_NOTE = 1000;

type Entry = AopIterationStatus['departments'][string];
type Action =
  | { kind: 'review'; onBehalfOf?: string }
  | { kind: 'approve'; tickFor?: string[]; submittedAt?: string | null; lastChangeAt?: string | null }
  | { kind: 'reopen'; note: string };

/** Ticks, actions and their refusals for one department — see the module note. */
export function AopReviewRow({ status, department, entry }: { status: AopIterationStatus; department: string; entry: Entry | undefined }) {
  const queryClient = useQueryClient();
  const [noteFor, setNoteFor] = useState<'reopen' | null>(null);
  /** The open "Approve now" confirmation: what it showed when it opened, which is what it sends. */
  const [approveNowSeen, setApproveNowSeen] = useState<{
    submittedAt: string | null;
    lastChangeAt: string | null;
    tickFor: string[];
    names: string;
  } | null>(null);
  const act = useMutation({
    mutationFn: (action: Action) => {
      if (action.kind === 'review') return reviewAopDepartment(department, action.onBehalfOf);
      if (action.kind === 'approve') {
        return action.tickFor
          ? approveAopDepartment(department, {
              tickMissing: true,
              submittedAt: action.submittedAt ?? null,
              lastChangeAt: action.lastChangeAt ?? null,
              tickFor: action.tickFor,
            })
          : approveAopDepartment(department);
      }
      return reopenAopDepartment(department, action.note);
    },
    // Pending until the NEW status is in, so no button is pressable on the old one.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
      setNoteFor(null);
      setApproveNowSeen(null);
    },
  });
  /** One action at a time: the DS Button's `loading` does not disable it (Enter, double clicks). */
  const run = (action: Action) => {
    if (!act.isPending) act.mutate(action);
  };
  /** True while THIS action runs — so only its own button spins. */
  const busyWith = (kind: Action['kind'], onBehalfOf?: string) =>
    act.isPending && act.variables?.kind === kind && (act.variables as { onBehalfOf?: string }).onBehalfOf === onBehalfOf;
  /** Open the "Approve now" confirmation fresh, capturing the submission and the reviewers it
   * names — never while another action is in flight. */
  const openApproveNow = () => {
    if (act.isPending) return;
    act.reset();
    setApproveNowSeen({ submittedAt: entry?.submittedAt ?? null, lastChangeAt: entry?.lastChangeAt ?? null, tickFor: missingKeys, names: waitingList });
  };
  /** Open a note dialog fresh — never while another action is still in flight. */
  const openNote = (kind: 'reopen') => {
    if (act.isPending) return;
    act.reset();
    setNoteFor(kind);
  };

  const reviewers = status.reviewers ?? [];
  const reviews = entry?.reviews ?? {};
  const submitted = entry?.state === 'submitted';
  const approved = entry?.state === 'approved';
  const isReviewer = Boolean(status.reviewerKey);
  const canApprove = Boolean(status.canApprove);
  const allTicked = reviewers.length > 0 && reviewers.every((r) => reviews[r.key]);
  // A tick Anna made on this reviewer's behalf can still be confirmed by the reviewer themselves.
  const ownTick = reviews[status.reviewerKey as string];
  const ownTickMissing = submitted && isReviewer && (!ownTick || ownTick.onBehalf);
  const missingKeys = reviewers.filter((r) => !reviews[r.key]).map((r) => r.key);
  const waitingFor = reviewers.filter((r) => !reviews[r.key]).map((r) => r.name);
  const waitingList = waitingFor.length > 1 ? `${waitingFor.slice(0, -1).join(', ')} and ${waitingFor.at(-1)}` : waitingFor[0];
  // Who is still missing is what the chips say; the only other reason Approve is greyed out is drift.
  const approveBlockedBy = entry?.stale ? 'The drafts changed since it was submitted.' : null;
  const canApproveNow = submitted && canApprove && waitingFor.length > 0 && !entry?.stale;
  // A confirmation the state overtook — another tick, an approval, a withdrawal, a RESUBMISSION — is
  // dropped, never left naming nobody or re-aimed at a plan it did not show. (Adjusted while
  // rendering — no effect cascade.)
  const approveNowStillFits =
    Boolean(approveNowSeen) &&
    canApproveNow &&
    approveNowSeen?.submittedAt === (entry?.submittedAt ?? null) &&
    approveNowSeen?.lastChangeAt === (entry?.lastChangeAt ?? null) &&
    approveNowSeen?.tickFor.join() === missingKeys.join();
  if (approveNowSeen && !approveNowStillFits && !act.isPending) setApproveNowSeen(null);

  // The iteration this approval is for — "Include in [AOP 2027] Iteration I".
  const iterationName = status.current?.name ?? 'this iteration';
  const includeLabel = `Include in ${iterationName}`;

  // Nothing to show for a department that is neither submitted nor approved, nor to a viewer with
  // no part in the review — the state line above already says where it stands.
  if (!submitted && !(approved && canApprove)) return null;
  const showTicks = submitted && reviewers.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {showTicks && (
          <ul role="list" aria-label="Reviews" className="m-0 flex list-none flex-wrap items-start gap-2 p-0">
            {reviewers.map((r) => {
              const tick = reviews[r.key];
              return (
                <li key={r.key} className="flex flex-col items-start gap-0.5">
                  <Badge variant={tick ? 'green' : 'grey'} className="shrink-0">
                    <span aria-hidden>{tick ? '✓' : '○'}</span>
                    <span className="sr-only">{tick ? 'Reviewed:' : 'Not reviewed yet:'}</span> {r.name}
                  </Badge>
                  {/* Under the chip: a tick someone else made for this reviewer, by their name. */}
                  {tick?.onBehalf ? <span className="text-xs text-zinc-400">{' '}by {personName(tick.by)}</span> : null}
                </li>
              );
            })}
          </ul>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {ownTickMissing && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={act.isPending}
              loading={busyWith('review')}
              onClick={() => run({ kind: 'review' })}
            >
              Mark reviewed
            </Button>
          )}
          {submitted &&
            canApprove &&
            reviewers
              .filter((r) => !reviews[r.key] && r.key !== status.reviewerKey)
              .map((r) => (
                <Button
                  key={r.key}
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={act.isPending}
                  loading={busyWith('review', r.key)}
                  onClick={() => run({ kind: 'review', onBehalfOf: r.key })}
                >
                  Tick for {r.name}
                </Button>
              ))}
          {/* ONE approve button: "Approve" once all three ticked (disabled while the drafts changed),
              else "Include in <iteration>", which ticks for whoever is missing after a confirmation. */}
          {canApproveNow ? (
            <Button type="button" size="sm" disabled={act.isPending} onClick={openApproveNow}>
              {includeLabel}
            </Button>
          ) : (
            submitted &&
            canApprove && (
              <Button
                type="button"
                size="sm"
                disabled={!allTicked || Boolean(entry?.stale) || act.isPending}
                loading={busyWith('approve')}
                onClick={() => run({ kind: 'approve' })}
              >
                Approve
              </Button>
            )
          )}
          {approved && canApprove && (
            <Button type="button" size="sm" variant="outline" disabled={act.isPending} onClick={() => openNote('reopen')}>
              Reopen
            </Button>
          )}
        </span>
      </div>
      {/* Who is still missing is what the chips show; this says why Approve is greyed out. */}
      {submitted && canApprove && !canApproveNow && approveBlockedBy && (
        <p className="text-xs text-zinc-500">Approve: {approveBlockedBy}</p>
      )}
      {act.error && !noteFor && !approveNowSeen && (
        <p role="alert" className="text-red-700">
          {(act.error as Error).message}
        </p>
      )}
      {approveNowSeen && approveNowStillFits && (
        <Modal open onOpenChange={(open: boolean) => !open && !act.isPending && setApproveNowSeen(null)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle className="!text-lg !font-semibold">
                Include {department} in {iterationName}?
              </ModalTitle>
            </ModalHeader>
            <p className="text-sm text-zinc-600">
              You tick &ldquo;reviewed&rdquo; for {approveNowSeen.names} — recorded as by you — and approve its plan for{' '}
              {iterationName}, which locks the department.
            </p>
            {act.error && (
              <p role="alert" className="text-sm text-red-700">
                {(act.error as Error).message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" disabled={act.isPending} onClick={() => setApproveNowSeen(null)}>
                Cancel
              </Button>
              {/* Sends what the confirmation SHOWED, never the row as it is now. */}
              <Button
                type="button"
                size="sm"
                loading={busyWith('approve')}
                onClick={() =>
                  run({
                    kind: 'approve',
                    submittedAt: approveNowSeen.submittedAt,
                    lastChangeAt: approveNowSeen.lastChangeAt,
                    tickFor: approveNowSeen.tickFor,
                  })
                }
              >
                Tick and include
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
      {noteFor && (
        <NoteModal
          title={`Reopen ${department}`}
          explain="The department goes back to its head, unlocked, for this iteration only. The approval stays in its history."
          confirm="Reopen"
          pending={act.isPending}
          error={act.error ? (act.error as Error).message : null}
          onClose={() => setNoteFor(null)}
          onConfirm={(note) => run({ kind: noteFor, note })}
        />
      )}
    </div>
  );
}

/** A required note, then the action — for sending back and for reopening. */
function NoteModal({
  title,
  explain,
  confirm,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  title: string;
  explain: string;
  confirm: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const valid = note.trim().length > 0 && note.trim().length <= MAX_NOTE;
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-lg !font-semibold">{title}</ModalTitle>
        </ModalHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !pending) onConfirm(note.trim());
          }}
        >
          <p className="text-sm text-zinc-600">{explain}</p>
          <Textarea
            label="Note"
            rows={4}
            value={note}
            maxLength={MAX_NOTE}
            placeholder="What needs changing, and why."
            onChange={(event) => setNote((event.target as HTMLTextAreaElement).value)}
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!valid} loading={pending}>
              {confirm}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}
